import { Observable, Subject } from 'rxjs';
import { tap, delay, startWith, filter, takeUntil } from 'rxjs/operators';

import { OverlayRef, GlobalPositionStrategy } from '@angular/cdk/overlay';

import { SnScreenService, SnViewport } from '@lib/common/services';

import { SnDrawerContainer } from '../containers';
import { SnDrawerData, SnDrawerConfig } from '../models';
import { SnDrawerMode, SnDrawerPosition } from '../types';

let uniqueId = 0;

export class SnDrawerRef<T = any, D = any, R = any> {
  private _componentInstance: T;

  private _result: R;

  private readonly _dataChange$ = new Subject<D>();
  private readonly _afterClosed$ = new Subject<R>();
  private readonly _close$ = new Subject<R>();

  private readonly _modeChange$ = new Subject<SnDrawerMode>();
  private readonly _positionChange$ = new Subject<SnDrawerPosition>();
  private readonly _widthChange$ = new Subject<number>();
  private readonly _destroy$ = new Subject<number>();

  constructor(
    public readonly id: string = `sn-drawer-${uniqueId++}`,
    private readonly _overlayRef: OverlayRef,
    private readonly _screen: SnScreenService,
    private readonly _drawerInstance: SnDrawerContainer,
    private readonly _drawerData: SnDrawerData,
    private readonly _drawerConfig: SnDrawerConfig<D>,
  ) {
    this._updateMode(this._drawerConfig.mode);
    this._updateWidth(this._drawerConfig.width);
    this._updatePosition(this._drawerConfig.position);

    this._watchScreen();
  }

  get componentInstance(): T {
    return this._componentInstance;
  }
  set componentInstance(component: T) {
    if (this._componentInstance) {
      return;
    }

    this._componentInstance = component;
  }

  get data(): D {
    return this._drawerData['__drawerData__'];
  }
  set data(value: D) {
    this._drawerData['__drawerData__'] = value;
    this._dataChange$.next(value);
  }

  get dataChange$(): Observable<D> {
    return this._dataChange$.asObservable();
  }

  get result(): R {
    return this._result;
  }

  get backdropClick$(): Observable<MouseEvent> {
    return this._overlayRef.backdropClick();
  }

  get openStarted$(): Observable<void> {
    return this._drawerInstance.openStarted$;
  }

  get openEnded$(): Observable<void> {
    return this._drawerInstance.opened$;
  }

  get closeStarted$(): Observable<void> {
    return this._drawerInstance.closeStarted$;
  }

  get closeEnded$(): Observable<void> {
    return this._drawerInstance.closed$;
  }

  get afterClosed$(): Observable<R | undefined> {
    return this._afterClosed$.asObservable();
  }

  get close$(): Observable<R> {
    return this._close$.asObservable();
  }

  get modeChange$(): Observable<SnDrawerMode> {
    return this._modeChange$.asObservable();
  }

  get positionChange$(): Observable<SnDrawerPosition> {
    return this._positionChange$.asObservable();
  }

  get widthChange$(): Observable<number> {
    return this._widthChange$.asObservable();
  }

  get width(): number {
    return this._drawerConfig.width;
  }
  get mode(): SnDrawerMode {
    return this._drawerConfig.mode;
  }
  get position(): SnDrawerPosition {
    return this._drawerConfig.position;
  }

  get isModal(): boolean {
    return this._drawerInstance.mode === 'modal';
  }

  public setMode(mode: SnDrawerMode): this {
    if (this.mode !== mode) {
      this._updateMode(mode);
    }

    return this;
  }

  public setPosition(value: SnDrawerPosition): this {
    if (value !== this._drawerConfig.position) {
      this._drawerInstance
        .close()
        .pipe(
          tap(() => this._updatePosition(value)),
          delay(1),
          takeUntil(this._destroy$),
        )
        .subscribe(() => {
          this._drawerInstance.open();
        });
    }

    return this;
  }

  public setSize(width: number): this {
    if (width !== this._drawerConfig.width) {
      this._updateWidth(width);
    }

    return this;
  }

  public addPanelClass(classes: string | string[]): this {
    this._overlayRef.addPanelClass(classes);
    return this;
  }

  public removePanelClass(classes: string | string[]): this {
    this._overlayRef.removePanelClass(classes);
    return this;
  }

  public close(result?: R): void {
    this._result = result;

    this._drawerInstance
      .close()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._overlayRef.detachBackdrop();
        this._overlayRef.dispose();

        this._afterClosed$.next(result);
        this._afterClosed$.complete();

        this._modeChange$.complete();
        this._positionChange$.complete();
        this._widthChange$.complete();

        this._destroy$.next();
        this._destroy$.complete();
      });
  }

  private _updateWidth(width: number): void {
    this._drawerConfig.width = width;
    this._drawerInstance.width = width;
    this._widthChange$.next(width);
  }

  private _setAdaptiveMode(): void {
    if (this._screen.viewport === SnViewport.Web) {
      this._setSideMode();
    } else {
      this._setModalMode();
    }
  }

  private _setSideMode(): void {
    this._drawerInstance.mode = 'side';
    this._modeChange$.next('side');

    this._updateOverlay();
  }

  private _setModalMode(): void {
    this._drawerInstance.mode = 'modal';
    this._modeChange$.next('modal');

    this._updateOverlay();
  }

  private _updateMode(mode: SnDrawerMode): void {
    switch (mode) {
      case 'side':
        this._drawerConfig.mode = mode;
        this._setSideMode();
        break;
      case 'modal':
        this._drawerConfig.mode = mode;
        this._setModalMode();
        break;
      case 'adaptive':
        this._drawerConfig.mode = mode;
        this._setAdaptiveMode();
    }
  }

  private _updatePosition(position: SnDrawerPosition): void {
    const config = this._overlayRef.getConfig();
    const positionStrategy = config.positionStrategy as GlobalPositionStrategy;
    positionStrategy.left(position === 'left' ? '0' : 'auto');
    positionStrategy.right(position === 'right' ? '0' : 'auto');

    this._overlayRef.updatePosition();

    this._drawerConfig.position = position;
    this._drawerInstance.position = position;
    this._positionChange$.next(position);
  }

  private _updateOverlay(): void {
    const overlay = this._overlayRef;
    const mode = this._drawerInstance.mode;

    if (mode === 'side') {
      overlay.detachBackdrop();
    } else if (this._drawerConfig.hasBackdrop && !overlay.backdropElement) {
      (overlay as any)._attachBackdrop();
    }
  }

  private _watchScreen(): void {
    this._screen.viewportChange$
      .pipe(
        startWith(this._screen.viewport),
        filter(() => this._drawerConfig.mode === 'adaptive'),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._setAdaptiveMode();
      });

    this._drawerInstance.animationStarted$.pipe(takeUntil(this._destroy$)).subscribe((event) => {
      if (event.toState === 'void') {
        this._close$.next();
      }
    });
  }
}
